import React from 'react';
import {
  CardSection,
  CondensedLarge,
  DateUtil,
  Grid,
  GridColumn,
  Gutter,
  Layout,
  Paragraph,
  SectionHeader,
  useBreakpoint,
  Well,
} from '@axiom/ui';
import type { CandidateEngagementDetails } from '@axiom/validation';

import { CandidateUtil } from '../../utils/candidate-util';

export type EngagementDetailsTeamProps = {
  engagement: CandidateEngagementDetails;
  name?: string;
};

export const EngagementsViewTeam: React.FC<EngagementDetailsTeamProps> = ({
  engagement,
  name = 'ENGAGEMENTS_VIEW_TEAM',
}) => {
  const { isSmallScreen } = useBreakpoint();
  const talentPartnerUser = CandidateUtil.getTalentPartnerUser(
    engagement.candidate
  );

  const showOppContact =
    engagement.opportunity.ownerUser &&
    DateUtil.getDaysSinceNow(engagement.position?.startDate ?? '1970/01/01') <=
      10;

  return (
    <CardSection name={name}>
      <Layout direction="vertical" verticalGutter="16px">
        {engagement.opportunity.account?.cxPartner ? (
          <Well background="gray" name="ENGAGEMENTS_VIEW_TEAM_CX_PARTNER">
            <SectionHeader>
              {engagement.opportunity.account.cxPartner.fullName}
            </SectionHeader>
            <Paragraph>Relationship Manager</Paragraph>
            <Gutter bottom="8px" />
            <Paragraph name="ENGAGEMENTS_VIEW_TEAM_CX_PARTNER_DESCRIPTION">
              This is the expert at onboarding to a new Axiom engagement at{' '}
              {engagement.opportunity.accountName}. They can assist with
              interview prep, onboarding prep, and ensuring your engagement
              kicks off on the right foot.
            </Paragraph>
            <Gutter bottom="16px" />
            <CondensedLarge>
              <a
                data-test="ENGAGEMENTS_VIEW_TEAM_CX_PARTNER_MAILTO"
                href={`mailto:${engagement.opportunity.account.cxPartner.email}`}
                target="_blank"
                rel="noreferrer"
              >
                {engagement.opportunity.account.cxPartner.email}
              </a>
            </CondensedLarge>
          </Well>
        ) : null}
        {showOppContact || engagement.opportunity.account?.ownerUser ? (
          <Grid
            name="ENGAGEMENTS_VIEW_TEAM_MIDDLE_ROW"
            columns={
              showOppContact &&
              engagement.opportunity.account?.ownerUser &&
              !isSmallScreen
                ? 2
                : 1
            }
            stretched
          >
            {showOppContact ? (
              <GridColumn>
                <Well
                  background="gray"
                  name="ENGAGEMENTS_VIEW_TEAM_OPP_OWNER_USER"
                >
                  <SectionHeader>
                    {engagement.opportunity.ownerUser?.fullName}
                  </SectionHeader>
                  <Paragraph>Opportunity Contact</Paragraph>
                  <Gutter bottom="8px" />
                  <Paragraph>
                    For interview logistics only on this opportunity:
                    scheduling, prep materials, and coordination. This may or
                    may not be your talent partner.
                  </Paragraph>
                  <Gutter bottom="16px" />
                  <CondensedLarge>
                    <a
                      data-test="ENGAGEMENTS_VIEW_TEAM_OPP_OWNER_USER_MAILTO"
                      href={`mailto:${engagement.opportunity.ownerUser?.email}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {engagement.opportunity.ownerUser?.email}
                    </a>
                  </CondensedLarge>
                </Well>
                {engagement.opportunity.account?.ownerUser ? (
                  <Gutter
                    name="ACCOUNT_OWNERUSER_GUTTER"
                    bottom="16px"
                    gutterType="margin"
                    only="smallScreen"
                  />
                ) : null}
              </GridColumn>
            ) : null}
            {engagement.opportunity.account?.ownerUser ? (
              <GridColumn>
                <Well
                  background="gray"
                  name="ENGAGEMENTS_VIEW_TEAM_ACCOUNT_OWNERUSER"
                >
                  <SectionHeader>
                    {engagement.opportunity.account.ownerUser.fullName}
                  </SectionHeader>
                  <Paragraph>Engagement Contact</Paragraph>
                  <Gutter bottom="8px" />
                  <Paragraph>
                    Your go-to person for this client. Reach out for
                    pre-interview client intel, when you spot new opportunities
                    for Axiom, have questions about your engagement status, or
                    need to escalate client questions.
                  </Paragraph>
                  <Gutter bottom="16px" />
                  <CondensedLarge>
                    <a
                      data-test="ENGAGEMENTS_VIEW_TEAM_ACCOUNT_OWNERUSER_MAILTO"
                      href={`mailto:${engagement.opportunity.account.ownerUser.email}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {engagement.opportunity.account.ownerUser.email}
                    </a>
                  </CondensedLarge>
                </Well>
              </GridColumn>
            ) : null}
          </Grid>
        ) : null}
        <Well
          background="sky blue"
          name="ENGAGEMENTS_VIEW_TEAM_CANDIDATE_OWNER_USER"
        >
          <SectionHeader>{talentPartnerUser.fullName}</SectionHeader>
          <Paragraph>Your Talent Partner</Paragraph>
          <Gutter bottom="8px" />
          <Paragraph>
            This is your dedicated Axiom talent partner who can help with
            professional development, leadership opportunities, career planning,
            or scheduling regular check-ins.
          </Paragraph>
          <Gutter bottom="16px" />
          <CondensedLarge>
            <a
              data-test="ENGAGEMENTS_VIEW_TEAM_CANDIDATE_OWNER_USER_MAILTO"
              href={`mailto:${talentPartnerUser.email}`}
              target="_blank"
              rel="noreferrer"
            >
              {talentPartnerUser.email}
            </a>
          </CondensedLarge>
        </Well>
      </Layout>
    </CardSection>
  );
};
